import { ComponentConfig } from "@measured/puck";
import { VideoPlayer as VideoPlayerComponent } from "./VideoPlayer";
import { VideoPlayerProps } from "./types";
import { t } from "i18next";
import { MediaManagerField } from "../../fields/MediaManagerField";

export const VideoPlayer: ComponentConfig<VideoPlayerProps> = {
    label: "Video Player",
    fields: {
        src: {
            type: "custom",
            label: t("puck.blocks.video_player.src", "Video URL (MP4, YouTube or Vimeo)"),
            render: ({ name, onChange, value }) => (
                <MediaManagerField
                    value={value}
                    onChange={onChange}
                    label={t("puck.blocks.video_player.src", "Video URL (MP4, YouTube or Vimeo)")}
                    allowedMimeTypes={["video/*"]}
                    allowedExtensions={["mp4", "webm", "mov", "avi", "mkv", "flv"]}
                    placeholder={t(
                        "puck.blocks.video_player.src_placeholder",
                        "Paste an MP4, YouTube or Vimeo link, or select from media library",
                    )}
                />
            ),
        },
        poster: {
            type: "custom",
            label: t("puck.blocks.video_player.poster", "Preview image (optional)"),
            render: ({ name, onChange, value }) => (
                <MediaManagerField
                    value={value}
                    onChange={onChange}
                    label={t("puck.blocks.video_player.poster", "Preview image (optional)")}
                    allowedMimeTypes={["image/*"]}
                    allowedExtensions={["jpg", "jpeg", "png", "gif", "webp"]}
                    placeholder={t(
                        "puck.blocks.video_player.poster_placeholder",
                        "Enter poster image URL or select from media library",
                    )}
                />
            ),
        },
        width: {
            type: "text",
            label: t("puck.blocks.video_player.width", "Width"),
        },
        height: {
            type: "text",
            label: t("puck.blocks.video_player.height", "Height"),
        },
        alignment: {
            type: "radio",
            label: t("puck.blocks.video_player.alignment", "Alignment"),
            options: [
                { label: t("puck.blocks.video_player.alignment.left", "Left"), value: "left" },
                { label: t("puck.blocks.video_player.alignment.center", "Center"), value: "center" },
                { label: t("puck.blocks.video_player.alignment.right", "Right"), value: "right" },
            ],
        },
        borderRadius: {
            type: "text",
            label: t("puck.blocks.video_player.border_radius", "Border radius"),
        },

        muted: {
            type: "radio",
            label: t("puck.blocks.video_player.muted", "Muted by default"),
            options: [
                { label: t("puck.blocks.video_player.muted.true", "Yes"), value: "true" },
                { label: t("puck.blocks.video_player.muted.false", "No"), value: "false" },
            ],
        },
        loop: {
            type: "radio",
            label: t("puck.blocks.video_player.loop", "Loop"),
            options: [
                { label: t("puck.blocks.video_player.loop.true", "Yes"), value: "true" },
                { label: t("puck.blocks.video_player.loop.false", "No"), value: "false" },
            ],
        },
        autoplay: {
            type: "radio",
            label: t("puck.blocks.video_player.autoplay", "Autoplay"),
            options: [
                { label: t("puck.blocks.video_player.autoplay.true", "Yes"), value: "true" },
                { label: t("puck.blocks.video_player.autoplay.false", "No"), value: "false" },
            ],
        },
    },
    defaultProps: {
        src: "",
        width: "400px",
        height: "300px",
        alignment: "center",
        borderRadius: "8px",
        muted: false,
        loop: false,
        autoplay: false,
        poster: "",
    },
    render: ({ src, width, height, alignment, borderRadius, muted, loop, autoplay, poster }) => {
        // Convert string values from radio options to boolean
        const mutedBool = muted === "true" || muted === true;
        const loopBool = loop === "true" || loop === true;
        const autoplayBool = autoplay === "true" || autoplay === true;

        return (
            <VideoPlayerComponent
                src={src}
                width={width}
                height={height}
                alignment={alignment}
                borderRadius={borderRadius}
                muted={mutedBool}
                loop={loopBool}
                autoplay={autoplayBool}
                poster={poster}
            />
        );
    },
};
